/**
 * Very rough lat/lng → US state code mapper for the 20 most-populated
 * Tesla-market states. Not a substitute for real reverse geocoding — but
 * good enough to feed the app's climate/weather-model with a sensible
 * regional bias when we only have coords from Tesla's drive_state.
 *
 * Bounding boxes overlap intentionally: whichever matches first wins.
 * Returns null when nothing plausible matches (caller should keep the
 * user's manual selection in that case).
 */
const BOXES = [
  { s: "CA", min: [32.5, -124.5], max: [42.0, -114.1] },
  { s: "OR", min: [42.0, -124.6], max: [46.3, -116.5] },
  { s: "WA", min: [45.5, -124.8], max: [49.0, -116.9] },
  { s: "NV", min: [35.0, -120.0], max: [42.0, -114.0] },
  { s: "AZ", min: [31.3, -114.9], max: [37.0, -109.0] },
  { s: "UT", min: [37.0, -114.1], max: [42.0, -109.0] },
  { s: "ID", min: [42.0, -117.3], max: [49.0, -111.0] },
  { s: "MT", min: [44.3, -116.1], max: [49.0, -104.0] },
  { s: "WY", min: [41.0, -111.1], max: [45.0, -104.0] },
  { s: "CO", min: [37.0, -109.1], max: [41.0, -102.0] },
  { s: "NM", min: [31.3, -109.1], max: [37.0, -103.0] },
  { s: "ND", min: [45.9, -104.1], max: [49.0, -96.5] },
  { s: "SD", min: [42.5, -104.1], max: [45.9, -96.4] },
  { s: "TX", min: [25.8, -106.7], max: [36.6, -93.5] },
  { s: "OK", min: [33.6, -103.0], max: [37.0, -94.4] },
  { s: "KS", min: [37.0, -102.1], max: [40.0, -94.6] },
  { s: "NE", min: [40.0, -104.1], max: [43.0, -95.3] },
  { s: "IA", min: [40.4, -96.6], max: [43.5, -90.1] },
  { s: "MN", min: [43.5, -97.3], max: [49.4, -89.5] },
  { s: "WI", min: [42.5, -92.9], max: [47.1, -86.8] },
  { s: "MO", min: [36.0, -95.8], max: [40.6, -89.1] },
  { s: "AR", min: [33.0, -94.6], max: [36.5, -89.6] },
  { s: "LA", min: [28.9, -94.1], max: [33.0, -89.0] },
  { s: "MS", min: [30.2, -91.7], max: [35.0, -88.1] },
  { s: "FL", min: [29.5, -87.6], max: [30.6, -82.0] }, // panhandle — before AL/GA
  { s: "AL", min: [30.1, -88.5], max: [35.0, -84.9] },
  { s: "IL", min: [37.0, -91.5], max: [42.5, -87.5] },
  { s: "IN", min: [37.8, -88.1], max: [41.8, -84.8] },
  { s: "MI", min: [41.7, -90.4], max: [48.3, -82.4] },
  { s: "OH", min: [38.4, -84.9], max: [42.0, -80.5] },
  { s: "KY", min: [36.5, -89.6], max: [39.2, -81.9] },
  { s: "TN", min: [34.9, -90.3], max: [36.7, -81.6] },
  { s: "WV", min: [37.2, -82.7], max: [40.6, -77.7] },
  { s: "NY", min: [40.5, -79.8], max: [45.0, -71.9] },
  { s: "PA", min: [39.7, -80.5], max: [42.3, -74.7] },
  { s: "NJ", min: [38.9, -75.6], max: [41.4, -73.9] },
  { s: "CT", min: [40.9, -73.7], max: [42.1, -71.8] },
  { s: "RI", min: [41.1, -71.9], max: [42.0, -71.1] },
  { s: "MA", min: [41.2, -73.5], max: [42.9, -69.9] },
  { s: "VT", min: [42.7, -73.5], max: [45.0, -71.5] },
  { s: "NH", min: [42.7, -72.6], max: [45.3, -70.6] },
  { s: "ME", min: [43.0, -71.1], max: [47.5, -66.9] },
  { s: "DE", min: [38.4, -75.8], max: [39.9, -75.0] },
  { s: "MD", min: [37.9, -79.5], max: [39.7, -75.0] },
  { s: "DC", min: [38.79, -77.12], max: [38.99, -76.9] },
  { s: "VA", min: [36.5, -83.7], max: [39.5, -75.2] },
  { s: "NC", min: [33.8, -84.3], max: [36.6, -75.5] },
  { s: "SC", min: [32.0, -83.4], max: [35.2, -78.5] },
  { s: "GA", min: [30.4, -85.6], max: [35.0, -80.8] },
  { s: "FL", min: [24.4, -87.6], max: [31.0, -80.0] },
  { s: "AK", min: [51.0, -170.0], max: [72.0, -129.0] },
  { s: "HI", min: [18.9, -160.5], max: [22.5, -154.5] },
];

export function stateFromCoords(lat, lng) {
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  for (const b of BOXES) {
    if (
      lat >= b.min[0] &&
      lat <= b.max[0] &&
      lng >= b.min[1] &&
      lng <= b.max[1]
    ) {
      return b.s;
    }
  }
  return null;
}

/**
 * Coarse "nearest known Tesla-market city" lookup by great-circle distance.
 * Used to auto-seed the trip-planner origin when the vehicle's real GPS
 * comes online. Not a substitute for real reverse geocoding, but tuned
 * to the same city list that TRIP_PRESETS uses so downstream distance
 * estimation Just Works.
 */
const CITIES = [
  { name: "San Francisco",       lat: 37.77, lng: -122.42 },
  { name: "Los Angeles",         lat: 34.05, lng: -118.24 },
  { name: "San Diego",           lat: 32.72, lng: -117.16 },
  { name: "Reno",                lat: 39.53, lng: -119.81 },
  { name: "South Lake Tahoe",    lat: 38.93, lng: -119.98 },
  { name: "Las Vegas",           lat: 36.17, lng: -115.14 },
  { name: "Phoenix",             lat: 33.45, lng: -112.07 },
  { name: "Seattle",             lat: 47.61, lng: -122.33 },
  { name: "Portland",            lat: 45.52, lng: -122.68 },
  { name: "Denver",              lat: 39.74, lng: -104.99 },
  { name: "Salt Lake City",      lat: 40.76, lng: -111.89 },
  { name: "Austin",              lat: 30.27, lng: -97.74 },
  { name: "Dallas",              lat: 32.78, lng: -96.80 },
  { name: "Houston",             lat: 29.76, lng: -95.37 },
  { name: "Chicago",             lat: 41.88, lng: -87.63 },
  { name: "Detroit",             lat: 42.33, lng: -83.05 },
  { name: "Nashville",           lat: 36.16, lng: -86.78 },
  { name: "Atlanta",             lat: 33.75, lng: -84.39 },
  { name: "Miami",               lat: 25.76, lng: -80.19 },
  { name: "Orlando",             lat: 28.54, lng: -81.38 },
  { name: "Tampa",               lat: 27.95, lng: -82.46 },
  { name: "Washington DC",       lat: 38.90, lng: -77.04 },
  { name: "New York",            lat: 40.71, lng: -74.01 },
  { name: "Boston",              lat: 42.36, lng: -71.06 },
];

function distMi(a, b) {
  // Haversine, close enough for a "which city is nearest" call.
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 3959; // miles
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) *
      Math.cos(toRad(b.lat)) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

export function nearestKnownCity(lat, lng, maxMiles = 150) {
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  const here = { lat, lng };
  let best = null;
  let bestD = Infinity;
  for (const c of CITIES) {
    const d = distMi(here, c);
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  return best && bestD <= maxMiles ? best.name : null;
}
